import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  Heading,
  Box,
  Alert,
  AlertDescription,
  AlertTitle,
  AlertIcon,
  useToast,
  Text,
} from "@chakra-ui/react";
import PaymentMethodSelector from "components/PaymentMethodSelector";
import { useOverlaySpinner } from "components/OverlaySpinner";
import StatementDescriptor from "components/StatementDescriptor";
import EmptyState from "components/EmptyState";
import { CHECKOUT } from "flows/constants";
//import { useFlowConfig } from "flows/hooks";
import { getFinalConfig, useSessionCtx, noop, FLOW_STATE, parsePMIDFromFlowState } from "utils";
import { useGetAuthorizedInvoiceById, usePayInvoice } from "api/invoicing";
import TiliaAPIError from "api/TiliaAPIError";
import { TAX_CALCULATION_TYPES } from "api/invoicing/constants";

import TransactionSummary from "./TransactionSummary";
import Skeletons from "./Skeletons";
import SuccessTimer from "./SuccessTimer";

const defaultConfig = {
  chooser: {
    include: [],
    exclude: [],
    addCC: true,
    addPaypal: true,
  },
};

/**
 * CheckoutContainer - Container component that handles the logic around the Checkout flow
 * @param {CheckoutContainerProps} props
 * @returns JSX.Element
 */
export default function CheckoutContainer({
  config,
  onComplete,
  onSubmitting = noop,
  onUpdate,
  onError = noop,
  enableAutoRedirect,
  onAddPMOpen,
  onAddPMClose,
}) {
  //const flowConfig = useFlowConfig();
  const { formatMessage } = useIntl();
  const { sessionCtx } = useSessionCtx();
  const toast = useToast();
  const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
  const [paymentMethodError, setPaymentMethodError] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const handleLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const newlyCreatedPMID = parsePMIDFromFlowState(sessionCtx.flowState);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [pmidErrorList, setPmidErrorList] = useState([]);

  const handleChange = useCallback(
    (values) => {
      const { usedWallets, usedPaymentMethod, isValid } = values;
      if (isValid) {
        let paymentMethods = [];

        paymentMethods = usedWallets.map((wallet) => ({
          payment_method_id: wallet.id,
          amount: wallet.amountUsed,
          isWallet: true,
        }));

        if (usedPaymentMethod) {
          paymentMethods.push({
            payment_method_id: usedPaymentMethod.id,
            amount: 0,
            isWallet: false,
          });
        }
        const length = paymentMethods.length;

        // exactly 1 PM has to have amount as 0, indicating to take everything that's remainder from the PM
        // our values are somewhat sorted, usedWallets last entry is the wallet we take the remaining from
        // if there is a usedPaymentMethod, then the remainder is taken from that PM instead
        // conveniently, this means the very last PM is responsible for paying for the remainder.
        if (length) {
          paymentMethods[length - 1].amount = 0;
        }

        setSelectedPaymentMethods(paymentMethods);
      }
    },
    [setSelectedPaymentMethods],
  );

  const invoiceQuery = useGetAuthorizedInvoiceById(
    sessionCtx.authorizedInvoiceId,
    selectedPaymentMethods,
  );

  const isPMRelatedCreateInvoiceError =
    invoiceQuery.isError && (invoiceQuery.error?.data?.codes || []).includes("INVALID_ADDRESS");

  const currency = invoiceQuery.data?.payload?.line_items_currency ?? null;
  const totalAmount = invoiceQuery.data?.payload?.line_items_total ?? null;
  const transactionDetails = {
    capability: "purchase",
    transaction_amount: totalAmount,
    currency,
  };

  const { mutateAsync: payInvoice, isSuccess: isPaymentSuccessful } = usePayInvoice();

  const { chooser: chooserConfig } = useMemo(
    () => getFinalConfig(CHECKOUT, defaultConfig, config),
    [config],
  );

  const statementDescriptor = chooserConfig.statementDescriptor;

  const handleDoneButtonPress = () => {
    onComplete({
      state: FLOW_STATE.COMPLETE,
      trigger: "done-button",
    });
  };

  const handleSubmit = async () => {
    try {
      onSubmitting(true);
      showOverlaySpinner();

      let invoiceId = null;
      if (sessionCtx.isEscrow) {
        invoiceId = invoiceQuery.data?.payload?.escrow_id ?? null;
      } else {
        invoiceId = invoiceQuery.data?.payload?.invoice_id ?? null;
      }

      if (!invoiceId) {
        throw new Error("Invoice ID missing");
      }

      await payInvoice({ invoiceId });

      if (onUpdate) {
        let updatePayload = {
          flow: CHECKOUT,
          type: "payment-processed",
        };
        if (sessionCtx.isEscrow) {
          updatePayload.escrow_id = invoiceId;
          updatePayload.message = formatMessage({
            defaultMessage: "Payment has been processed successfully and escrow has been created.",
            description: "Payment status message",
          });
        } else {
          updatePayload.invoice_id = invoiceId;
          updatePayload.message = formatMessage({
            defaultMessage: "Payment has been processed successfully.",
            description: "Payment status message",
          });
        }
        onUpdate(updatePayload);
      }
    } catch (error) {
      onSubmitting(false);
      onError(error);

      // setSelectedPaymentMethods([]); // TODO: remove if we don't actually need this here.  Not having this fixes an issue where we reload the authorized invoice when (I believe) we don't need to

      toast({
        title: formatMessage({
          defaultMessage: "Error processing payment",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Sorry, we were unable to process your payment. Please try again later.",
          description: "Popup description",
        }),
        status: "error",
        isClosable: true,
      });
    } finally {
      hideOverlaySpinner();
    }
  };

  const handleError = (paymentMethodError) => {
    setPaymentMethodError(paymentMethodError);
  };

  /**
   * useEffect to monitor pmidErrorList,
   * when an item is added this only happens
   * when there was an issue with an invoice
   * being created with it, so we show a toast
   * error message
   */
  useEffect(() => {
    if (pmidErrorList.length > 0) {
      toast({
        title: formatMessage({
          defaultMessage: "Error with selected payment method",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Please select another payment option.",
          description: "Popup description",
        }),
        status: "error",
        isClosable: true,
      });
    }
  }, [pmidErrorList, formatMessage, toast]);

  if ((invoiceQuery.isError && !isPMRelatedCreateInvoiceError) || paymentMethodError) {
    const error = invoiceQuery.error || paymentMethodError;

    // if we have reached this point but then get a 404 when requesting invoice,
    // then we are likely dealing with a line items payload key that redis can't find
    const invalidLineItemsKey = error instanceof TiliaAPIError && error.status === 404;
    const message = invalidLineItemsKey
      ? formatMessage({
          defaultMessage:
            "Invalid link. This is most likely caused by an expired link, or an invoice that has already been processed.",
          description: "Error message",
        })
      : formatMessage({
          defaultMessage: "Unable to fetch invoice data",
          description: "Error message",
        });
    onError({
      message,
      error,
    });
    let content = <EmptyState error={error} />;
    if (invalidLineItemsKey) {
      content = (
        <EmptyState
          title={formatMessage({
            defaultMessage: "Invalid link",
            description: "Status title",
          })}
          description={formatMessage({
            defaultMessage:
              "This is most likely caused by an expired link, or an invoice that has already been processed.",
            description: "Status message",
          })}
        />
      );
    }
    return content;
  }

  if (isPMRelatedCreateInvoiceError) {
    // we received a create invoice error that is a result of
    // missing address information tied to the payment method.
    // let's keep track of the id of the pm so we can disable
    // it and show an error message for the user to select another
    setPmidErrorList([
      ...pmidErrorList, // make sure we have any previous errors still included
      ...selectedPaymentMethods.filter((pm) => !pm.isWallet).map((pm) => pm.payment_method_id), // pmid of currently selected payment method since there should only be 1 non-wallet payment method
    ]);
    setSelectedPaymentMethods([]);
    return <Skeletons />;
  }

  if (invoiceQuery.isLoading) {
    return <Skeletons />;
  }

  const lineItems = invoiceQuery.data?.payload?.line_items ?? [];
  const referenceType = invoiceQuery.data?.payload?.reference_type ?? null;
  const referenceId = invoiceQuery.data?.payload?.reference_id ?? null;
  const includesTax = Boolean(invoiceQuery.data?.payload?.summary?.tax?.total_amount);
  const taxTotalAmount = includesTax
    ? invoiceQuery.data?.payload?.summary?.tax?.total_amount
    : null;
  const subtotalAmount = invoiceQuery.data?.payload?.summary?.subtotal?.total_amount ?? null;
  const isTaxInclusive =
    invoiceQuery.data?.payload?.summary?.tax_calculation === TAX_CALCULATION_TYPES.INCLUSIVE;
  const fullInvoiceNotLoadedYet = !invoiceQuery.data?.payload?.invoice_id;
  const disableSubmit = invoiceQuery.isFetching || fullInvoiceNotLoadedYet;

  const params = [
    transactionDetails,
    {
      enabled: invoiceQuery.isSuccess,
    },
  ];

  let paymentMethodsUsedAmount = invoiceQuery.data?.payload?.payment_methods || [];

  paymentMethodsUsedAmount = Object.keys(paymentMethodsUsedAmount).map(
    (key) => paymentMethodsUsedAmount[key],
  );

  const paymentMethodsOrSuccessMessage = () => {
    if (isPaymentSuccessful) {
      return (
        <Box mb={4}>
          <SuccessTimer onComplete={onComplete} />
          <Alert status="success" variant="solid" mb={4}>
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>
                <FormattedMessage defaultMessage="Success!" description="Status title" />
              </AlertTitle>
              <AlertDescription display="block">
                <FormattedMessage
                  defaultMessage="Payment has been processed."
                  description="Status description"
                />
              </AlertDescription>
            </Box>
          </Alert>

          <Button
            onClick={handleDoneButtonPress}
            variant="outline"
            colorScheme="gray"
            id="payment-success-done-button"
            width="full"
          >
            <FormattedMessage defaultMessage="Done" description="Button label" />
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {!isLoading && (
          <>
            <Heading mb={2}>
              <FormattedMessage
                defaultMessage="Choose your payment method"
                description="Header title"
              />
            </Heading>
            <Text fontSize="sm">
              <FormattedMessage
                defaultMessage="Please select your preferred payment method or add a new one by clicking the link below"
                description="sub header"
              />
            </Text>
          </>
        )}

        <PaymentMethodSelector
          params={params}
          amount={totalAmount}
          paymentMethodsUsedAmount={paymentMethodsUsedAmount}
          chooserConfig={chooserConfig}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onError={handleError}
          onLoading={handleLoading}
          enableAutoRedirect={enableAutoRedirect}
          onAddPMOpen={onAddPMOpen}
          onAddPMClose={onAddPMClose}
          disableSubmitButton={disableSubmit}
          pmidErrorList={pmidErrorList}
          preferredPaymentMethodId={newlyCreatedPMID}
        />
        {!isLoading && statementDescriptor && (
          <StatementDescriptor mt="100px" descriptor={statementDescriptor} />
        )}
      </Box>
    );
  };

  return (
    <Box>
      <TransactionSummary
        lineItems={lineItems}
        referenceType={referenceType}
        referenceId={referenceId}
        subtotalAmount={subtotalAmount}
        taxTotalAmount={taxTotalAmount}
        totalAmount={totalAmount}
        currency={currency}
        isLoadingNewTotal={invoiceQuery.isFetching}
        isTaxInclusive={isTaxInclusive}
      />

      {paymentMethodsOrSuccessMessage()}
    </Box>
  );
}
